/**
 * UI シェル。ルート DOM を整え、エンジンの描画先・ツールバー・パネル・
 * 支援技術向け領域を組み立てる（FR-UI）。
 */
import { cls, cssVar } from '../brand';
import { Announcer } from '../a11y/announcer';
import { escapeClaimed } from '../a11y/dialog';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { OutlineNode } from '../core/manifest';
import type { ShareOptions, UiOptions } from '../core/options';
import type { ViewerState } from '../core/viewer';
import type { Translate } from '../i18n/i18n';
import { SharePanel } from '../link/share-panel';
import { SearchPanel } from '../search/search-panel';
import type { ViewerApi } from './api';
import { CommentsPanel } from './comments-panel';
import { Help } from './help';
import { SpeechPanel } from './speech-panel';
import { SpeechReader, speechHostOf } from './speech-reader';
import { bindInput } from './input';
import { Lightbox } from './lightbox';
import { MarksPanel } from './marks-panel';
import { Outline } from './outline';
import { PanPad } from './pan-pad';
import { PageTextView } from './page-text';
import type { PageTextEntry } from './page-text';
import { Panels } from './panels';
import { Preloader } from './preloader';
import { Thumbnails } from './thumbnails';
import { Toolbar } from './toolbar';
import type { ControlDef } from './toolbar';

export class Shell {
  /** エンジンの描画先。 */
  readonly host: HTMLElement;
  readonly lightbox: Lightbox;

  private readonly doc: Document;
  private readonly announcer: Announcer;
  /** 誌面の代替テキスト（canvas は aria-hidden なので中身はここに出る）。 */
  private readonly pageText: PageTextView;
  /** 読み上げ（M11）。ブラウザが口を持たない環境では null のまま＝操作子も出さない。 */
  private speech: SpeechReader | null = null;
  /** 読み上げへ渡す本文（最後に出した代替テキストと誌面の言語）。 */
  private speechSegments: readonly { text: string; lang?: string }[] = [];
  private speechLang = 'ja';
  private readonly panels: Panels;
  /** 拡大中に出るパン操作パッド（WCAG 2.1.1・2.5.1）。 */
  private readonly panPad: PanPad;
  private toolbar: Toolbar | null = null;
  /** ツールバーの実寸を追う監視（折り返しで高さが変わる）。 */
  private toolbarResize: ResizeObserver | null = null;
  private thumbnails: Thumbnails | null = null;
  /** 起動中の表示（ready / error で撤去し、以後 null）。 */
  private preloader: Preloader | null = null;
  private readonly restore: { tabIndex: string | null; role: string | null };

  constructor(
    private readonly root: HTMLElement,
    private readonly registry: DisposeRegistry,
    private readonly api: ViewerApi,
    private readonly translate: Translate,
    private readonly ui: UiOptions,
    private readonly searchMinChars: number,
    private readonly share: ShareOptions,
  ) {
    this.doc = root.ownerDocument;
    // ルートは埋め込み先の要素なので、こちらが上書きする属性の元の値を控えておく。
    this.restore = { tabIndex: root.getAttribute('tabindex'), role: root.getAttribute('role') };

    root.classList.add(cls('viewer'));
    // ルートは「名前のある操作子のまとまり」。役割を与えないと `aria-label` が
    // 落ちて名前が伝わらない（役割の無い要素への aria-label は禁止・axe が拾う）。
    // ランドマークにはしない — 埋め込み先の見出し構造を荒らさないため。
    root.setAttribute('role', 'group');
    root.setAttribute('aria-label', translate('viewer.label'));
    // キーボード操作の起点にするため、ルート自体をフォーカス可能にする。
    root.setAttribute('tabindex', '0');

    this.host = this.doc.createElement('div');
    this.host.className = cls('surface');
    // `role="application"` は**誌面だけ**に付ける（M8c-2 手順 6）。ルートに付けると
    // ツールバーもパネルも支援技術の読み上げモードから外れ、一覧や本文を読み進める
    // 手段を奪ってしまう。矢印キーが「めくり」を意味するのは誌面の上だけなので、
    // モードを切り替える範囲もそこに限る。キーの受け口（root）は動かしていない。
    this.host.setAttribute('role', 'application');
    this.host.setAttribute('aria-label', translate('viewer.surface'));

    this.panels = new Panels(this.doc, registry, translate);
    this.announcer = new Announcer(this.doc);
    this.pageText = new PageTextView(this.doc);
    // パン操作パッドはツールバーより前に置く（ツールバーが DOM でも視覚でも最後、
    // という読み順を崩さないため — E2E-TAB-ORDER が守っている不変条件）。
    this.panPad = new PanPad(this.doc, registry, api, this.host, translate);

    root.append(this.host, this.panels.el, this.panPad.el);
    // 代替テキストは誌面の直後（＝`role="application"` の外側）に置く。
    this.pageText.mount(root, this.panels.el);
    this.announcer.mount(root);

    // 起動表示は build() より前＝生成直後から出す（初期化中の空箱を見せない）。
    this.preloader = new Preloader(this.doc, translate);
    this.preloader.mount(root);

    this.lightbox = new Lightbox(root, registry, () => this.sync());

    // Esc は**内側から 1 段ずつ**閉じる（FR-A11Y-01）。ここで先にパネルを引き取り、
    // 処理したら preventDefault する。Lightbox は defaultPrevented を見て降りるので、
    // パネルとライトボックスが同時に開いていても 1 回の Esc で両方は閉じない。
    // この listener を（Lightbox が show() で張るものより先に）コンストラクタで
    // 登録しておくことで、同一 target・同一フェーズでの発火順が確定する。
    registry.listen(this.doc, 'keydown', (event) => this.onKeydown(event as KeyboardEvent), true);

    bindInput(root, this.host, registry, api);
  }

  /** Esc で開いているパネルを閉じる（開いていなければ何もせず Lightbox へ譲る）。 */
  private onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    // 上に重なったモーダル（注釈ノート）が先に閉じる — いちばん内側から 1 段ずつ
    if (escapeClaimed()) return;
    if (this.panels.openId === null) return;
    event.preventDefault();
    this.togglePanel(this.panels.openId);
  }

  /**
   * データが揃ってから組み立てる部分（ページ数・目次が要るもの）。
   * 読み込み前にツールバーだけ出して空振りさせない。
   */
  build(pageCount: number, outline: readonly OutlineNode[], textAvailable: boolean): void {
    // 目次を持たない本では目次ボタンを出さない（開いても空のパネルにしかならないため）。
    // 索引の無い本（テキスト層なし）では検索を出さない（FR-SRC-07 の縮退）。
    // 読み上げは (1) 設定で有効 (2) ブラウザが口を持つ (3) 読む本文がある（テキスト層）
    // の 3 つが揃ったときだけ出す。どれか欠けたまま操作子を出すと、押しても何も起きない。
    const host = speechHostOf(this.doc.defaultView ?? window);
    if (this.ui.speech && textAvailable && host !== null) {
      this.speech = new SpeechReader(host, this.registry);
    }
    const ui = {
      ...this.ui,
      outline: this.ui.outline && outline.length > 0,
      search: this.ui.search && textAvailable,
      speech: this.speech !== null,
    };

    if (ui.toolbar) {
      this.toolbar = new Toolbar(this.doc, this.registry, this.api, this.translate, ui, (id) =>
        this.togglePanel(id),
      );
      this.root.appendChild(this.toolbar.el);
      // ツールバーの分だけ描画面を狭める（本がツールバーの下に潜らないように）。
      this.root.classList.add(cls('has-toolbar'));
      this.watchToolbarHeight(this.toolbar.el);
    }

    if (ui.thumbnails) {
      this.thumbnails = new Thumbnails(
        this.doc,
        this.registry,
        this.api,
        this.translate,
        pageCount,
      );
      this.panels.register('thumbnails', this.translate('panel.thumbnails'), this.thumbnails);
    }

    if (ui.outline) {
      this.panels.register(
        'outline',
        this.translate('panel.outline'),
        new Outline(this.doc, this.registry, this.api, outline),
      );
    }

    if (ui.search) {
      this.panels.register(
        'search',
        this.translate('panel.search'),
        new SearchPanel(this.doc, this.registry, this.api, this.translate, this.searchMinChars),
      );
    }

    if (ui.marks) {
      this.panels.register(
        'marks',
        this.translate('panel.marks'),
        new MarksPanel(this.doc, this.registry, this.api, this.translate),
      );
    }

    if (ui.comments) {
      this.panels.register(
        'comments',
        this.translate('panel.comments'),
        new CommentsPanel(this.doc, this.registry, this.api, this.translate),
      );
    }

    if (ui.help) {
      this.panels.register('help', this.translate('panel.help'), new Help(this.doc, this.translate));
    }

    const speech = this.speech;
    if (ui.speech && speech !== null) {
      this.panels.register(
        'speech',
        this.translate('panel.speech'),
        new SpeechPanel(this.doc, this.registry, this.translate, speech, {
          segments: () => this.speechSegments,
          lang: () => this.speechLang,
        }),
      );
    }

    if (ui.share) {
      this.panels.register(
        'share',
        this.translate('panel.share'),
        new SharePanel(
          this.doc,
          this.registry,
          this.api,
          this.translate,
          this.share,
          (message) => this.announce(message),
        ),
      );
    }
  }

  /** 操作ヘルプの開閉（`?` キーと公開 API から）。 */
  toggleHelp(): void {
    this.togglePanel('help');
  }

  /** 読み込みの割合（0–1）を起動表示へ渡す。進捗を取れる配信モードだけが呼ぶ。 */
  setProgress(ratio: number): void {
    this.preloader?.setProgress(ratio);
  }

  /** 起動表示を撤去する（ready でも error でも呼ぶ）。 */
  hidePreloader(): void {
    this.preloader?.dispose(this.root);
    this.preloader = null;
  }

  /**
   * 起動表示を出し直す（解錠のように**人の入力で中断した読み込み**を再開したとき）。
   * すでに出ていれば何もしない。
   */
  showPreloader(): void {
    if (this.preloader) return;
    this.preloader = new Preloader(this.doc, this.translate);
    this.preloader.mount(this.root);
  }

  /** 追加ボタン（FR-UI-03）。 */
  addControl(def: ControlDef): void {
    this.toolbar?.addControl(def);
  }

  /** 状態を UI へ反映する。 */
  sync(state: ViewerState = this.api.getState()): void {
    this.toolbar?.update(state, this.panels.openId);
    this.thumbnails?.update(state.page);
    this.panels.sync(state);
    this.panPad.update(state);
  }

  announce(message: string): void {
    this.announcer.announce(message);
  }

  /** 表示中ページの代替テキストを差し替える（見開きなら 2 ページ分）。 */
  setPageText(entries: readonly PageTextEntry[]): void {
    this.pageText.set(entries);
    // 読み上げは代替テキストと同じものを読む（見出し行は読まない — 本文だけ）。
    // ページが変わったら黙る: 別のページの本文を読み続けるのは利用者の意図と違う。
    const segments = entries.flatMap((entry) =>
      entry.lines.flatMap((line) => (typeof line === 'string' ? [{ text: line }] : [...line])),
    );
    const changed = segments.map((s) => s.text).join('') !== this.speechSegments.map((s) => s.text).join('');
    this.speechSegments = segments;
    if (changed) this.speech?.stop();
  }

  /** 誌面本文の言語（マニフェスト由来）を代替テキストの器へ置く（WCAG 3.1.2）。 */
  setPageTextLang(lang: string): void {
    this.pageText.setLang(lang);
    this.speechLang = lang;
  }

  private togglePanel(id: string): void {
    this.panels.toggle(id);
    this.sync();
  }

  /**
   * ツールバーの実寸を描画面へ伝える（FR-A11Y／WCAG 1.4.10・1.4.4）。
   *
   * 描画面が空ける下端は CSS では既定 48px の 1 段ぶんしかない。ツールバーは
   * `flex-wrap` で折り返すため、狭い幅やページズームでは 2〜4 段に伸び、
   * **伸びた分だけ誌面がツールバーの下へ潜って読めなくなる**（320 CSS px 幅で
   * 誌面の 3 分の 1 が隠れることを実測）。段数を数えるのではなく実寸を測るのは、
   * 段数が言語・オプション・導入者の装飾で変わるため。
   */
  private watchToolbarHeight(el: HTMLElement): void {
    const apply = (): void => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h > 0) this.root.style.setProperty(cssVar('toolbar-height'), `${h}px`);
    };
    apply();
    // 実行環境に ResizeObserver が無ければ初回の実寸のみ（既定値よりは正確）。
    if (typeof ResizeObserver === 'undefined') return;
    this.toolbarResize = new ResizeObserver(apply);
    this.toolbarResize.observe(el);
  }

  /** ルート要素を生成前の状態へ戻す。 */
  dispose(): void {
    this.hidePreloader();
    this.lightbox.dispose();
    this.toolbarResize?.disconnect();
    this.toolbarResize = null;
    this.root.style.removeProperty(cssVar('toolbar-height'));
    this.toolbar?.dispose();
    this.panPad.dispose();
    this.panels.dispose();
    this.announcer.dispose();
    this.pageText.dispose();
    this.host.remove();

    this.root.classList.remove(cls('viewer'), cls('has-toolbar'));
    this.root.removeAttribute('aria-label');
    for (const [name, value] of [
      ['tabindex', this.restore.tabIndex],
      ['role', this.restore.role],
    ] as const) {
      if (value === null) this.root.removeAttribute(name);
      else this.root.setAttribute(name, value);
    }
  }
}
